import { useCallback, useState } from 'react';
import { proctoringPingUrl } from '../../ai-interview/utils/aiInterviewApi';

/**
 * Measures connection quality by hitting the backend /ping probe several times: average latency,
 * jitter (variation between samples), and packet loss (failed probes). Maps to a rating the
 * candidate sees and the backend stores.
 */
const SAMPLES = 8;

function rate(pingMs, lossPct) {
  if (pingMs == null) return 'Poor';
  if (lossPct === 0 && pingMs < 80) return 'Excellent';
  if (lossPct < 2 && pingMs < 150) return 'Good';
  if (lossPct < 5 && pingMs < 300) return 'Fair';
  return 'Poor';
}

export default function useNetworkTest() {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  const run = useCallback(async () => {
    setRunning(true);
    setProgress(0);
    setResult(null);
    const url = proctoringPingUrl();
    const rtts = [];
    let failures = 0;

    for (let i = 0; i < SAMPLES; i += 1) {
      const t0 = performance.now();
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(`${url}?n=${i}`, { method: 'GET', cache: 'no-store' });
        if (!res.ok) throw new Error('bad');
        // eslint-disable-next-line no-await-in-loop
        await res.json().catch(() => ({}));
        rtts.push(performance.now() - t0);
      } catch {
        failures += 1;
      }
      setProgress(Math.round(((i + 1) / SAMPLES) * 100));
      // small spacing so jitter is meaningful
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 120));
    }

    const lossPct = Math.round((failures / SAMPLES) * 100);
    let pingMs = null;
    let jitterMs = null;
    if (rtts.length) {
      pingMs = Math.round(rtts.reduce((a, b) => a + b, 0) / rtts.length);
      // jitter = mean absolute consecutive difference
      let diffs = 0;
      for (let i = 1; i < rtts.length; i += 1) diffs += Math.abs(rtts[i] - rtts[i - 1]);
      jitterMs = rtts.length > 1 ? Math.round(diffs / (rtts.length - 1)) : 0;
    }
    const out = {
      ping_ms: pingMs,
      jitter_ms: jitterMs,
      packet_loss_pct: lossPct,
      samples: SAMPLES,
      rating: rate(pingMs, lossPct),
    };
    setResult(out);
    setRunning(false);
    return out;
  }, []);

  return { run, running, progress, result };
}
